import { type PostCaps, postPermissions, userHasPostCap } from '@/Components/posts/status';
import { Analytics } from '@/Telemetry';
import { CookieHandler } from '@/Telemetry/cookie-init';
import {
  type Auth_LoginWithProvider_Mutation,
  type Auth_Logout_Mutation,
  loginWithProvidersMutation,
  logoutMutation,
} from '@/graphql/mutations/Auth.mutation';
import type { Frags_With_Author_Fragment$data, Frags_Wiz_Author_Fragment$data } from '@/graphql/queries/Frags.query';
import type { User_Fragment$data } from '@/graphql/queries/User.query';
import { type ZnglyContext_User_Query$data, userQueryRef } from '@/graphql/queries/ZnglyContext.query';
import type { FragmentData, GenericMutation } from '@/graphql/utils';
import { ZnglyAppFallback } from '@/inertia/Shared/Page';
import React, { useMemo } from 'react';
import { create } from 'zustand';

const initUseUser = {
  id: '' as string,
  userExists: false as boolean,
  isUser: false as boolean,
  isGuest: true as boolean,
  isAdmin: false as boolean,
  isSuperAdmin: false as boolean,
  role: 'guest' as 'admin' | 'super_admin' | 'user' | 'guest' | 'user_sales' | 'user_sales_restricted',
  user: null as FragmentData<User_Fragment$data> | null | undefined,
};

export type UserType = typeof initUseUser;

export type AuthStore = {
  signin: (
    user: string,
    password: string,
    onCompleted: GenericMutation<Auth_LoginWithProvider_Mutation>['onCompleted']
  ) => void;
  signout: (onCompleted?: GenericMutation<Auth_Logout_Mutation>['onCompleted']) => void;
  setUser: (user: FragmentData<User_Fragment$data> | null | undefined) => void;
  user: UserType;
};

const userFormat = (user?: FragmentData<User_Fragment$data> | null | undefined): UserType => {
  return {
    ...initUseUser,
    id: user?.id ?? '',
    isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
    isSuperAdmin: user?.role === 'super_admin',
    isUser: user?.role === 'user',
    isGuest: user?.role === 'guest' || !user?.id,
    role: (user?.role as UserType['role']) ?? 'guest',
    userExists: !!user?.id,
    user,
  };
};

export const useAuthStore = create<AuthStore>((set) => ({
  user: userFormat(),
  setUser(user) {
    set({ user: userFormat(user) });
  },
  signin: (
    user: string,
    password: string,
    onCompleted: GenericMutation<Auth_LoginWithProvider_Mutation>['onCompleted']
  ) => {
    loginWithProvidersMutation(
      {
        input: {
          provider: 'PASSWORD',
          credentials: {
            username: user,
            password,
          },
        },
      },
      {
        onCompleted: (data, errors) => {
          onCompleted?.(data, errors);
          if (!!errors?.length) {
            return;
          }

          Analytics.push(
            'login',
            {
              value: data?.login?.user?.id,
              loggedInUserId: data?.login?.user?.id,
              eventaction: 'login',
            },
            false
          );

          set({
            user: { ...userFormat(data?.login?.user) },
          });

          window.location.href = `${window.location.origin}/dash/catalog`;
        },
      }
    );
  },
  signout: (onCompleted?: GenericMutation<Auth_Logout_Mutation>['onCompleted']) => {
    logoutMutation(
      { input: {} },
      {
        onCompleted: (data, errors) => {
          onCompleted?.(data, errors);
          if (!!errors?.length) return;

          if (data?.logout?.success === false) {
            return;
          }

          set({
            user: { ...initUseUser },
          });

          window.location.href = `${window.location.origin}/auth/login`;
        },
      }
    );
  },
}));

/**
 * AuthProvider
 * Block the app until the user query is resolved
 * Once the user query is resolved, set the user in the store and render the app
 */
export const AuthProvider = (props: { children: React.ReactNode }) => {
  const subscribeRef = React.useRef(
    new Promise<boolean>((resolve) => {
      userQueryRef.source?.subscribe({
        complete: () => {
          resolve(true);
        },
        next(_value) {
          const viewer = (_value as unknown as { data: ZnglyContext_User_Query$data })?.data
            ?.viewer as FragmentData<User_Fragment$data>;

          useAuthStore.getState().setUser(viewer);

          if (window?.umami) {
            // @ts-ignore
            window.umami.identify({
              client_id: CookieHandler.getZnClientId(),
              ...(viewer?.id && { email: viewer?.email, id: viewer?.id }),
            });
          }
        },
        error: () => {
          resolve(false);
        },
      });
    })
  );
  const isInEffect = React.useRef(false);

  const [isUserReady, setIsUserReady] = React.useState(false);

  React.useEffect(() => {
    if (isUserReady || isInEffect.current) return;

    isInEffect.current = true;
    const fn = async () => {
      setIsUserReady(await subscribeRef.current);
    };
    fn();
  }, []);

  if (isUserReady) {
    return props.children;
  }

  return <ZnglyAppFallback />;
};

export type UserCaps =
  | PostCaps
  | 'switch_themes'
  | 'edit_themes'
  | 'activate_plugins'
  | 'edit_plugins'
  | 'edit_users'
  | 'edit_files'
  | 'manage_options'
  | 'moderate_comments'
  | 'manage_categories'
  | 'manage_links'
  | 'upload_files'
  | 'import'
  | 'unfiltered_html'
  | 'edit_posts'
  | 'edit_others_posts'
  | 'edit_published_posts'
  | 'publish_posts'
  | 'edit_pages'
  | 'read'
  | 'level_10'
  | 'level_9'
  | 'level_8'
  | 'level_7'
  | 'level_6'
  | 'level_5'
  | 'level_4'
  | 'level_3'
  | 'level_2'
  | 'level_1'
  | 'level_0'
  | 'edit_others_pages'
  | 'edit_published_pages'
  | 'publish_pages'
  | 'delete_pages'
  | 'delete_others_pages'
  | 'delete_published_pages'
  | 'delete_posts'
  | 'delete_others_posts'
  | 'delete_published_posts'
  | 'delete_private_posts'
  | 'edit_private_posts'
  | 'read_private_posts'
  | 'delete_private_pages'
  | 'edit_private_pages'
  | 'read_private_pages'
  | 'delete_users'
  | 'create_users'
  | 'unfiltered_upload'
  | 'edit_dashboard'
  | 'update_plugins'
  | 'delete_plugins'
  | 'install_plugins'
  | 'update_themes'
  | 'install_themes'
  | 'update_core'
  | 'list_users'
  | 'remove_users'
  | 'promote_users'
  | 'edit_theme_options'
  | 'delete_themes'
  | 'export'
  | 'view_all_aryo_activity_log'
  | 'wf2fa_activate_2fa_self'
  | 'wf2fa_activate_2fa_others'
  | 'wf2fa_manage_settings'
  | 'administrator';

/**
 * Test if user has caps
 */
export const userHasCaps = (user: UserType['user'], caps: UserCaps[]) => {
  if (!user) return false;

  return caps.every((cap) => user?.capabilities?.includes(cap));
};

export const useUserCaps = (caps: UserCaps[]) => {
  const { user } = useAuthStore.getState();

  return useMemo(() => userHasCaps(user.user, caps), [user, caps]);
};

/**
 * Given a post type, return the capabilities for that post type for the current user
 * @returns an object with the capabilities for the post type and true/false if the user has that capability
 */
export const useUserPostCaps = <T extends Parameters<typeof postPermissions>[0]>(
  type: T,
  postAuthor:
    | FragmentData<User_Fragment$data>
    | Frags_With_Author_Fragment$data['node']
    | Frags_Wiz_Author_Fragment$data['node']
    | null = null
): Record<keyof ReturnType<typeof postPermissions<T>>, boolean> => {
  const user = useAuthStore((state) => state?.user);

  const userCaps = useMemo(() => {
    const permissions = postPermissions(type);

    const ret = {} as Record<keyof ReturnType<typeof postPermissions<T>>, boolean>;
    for (const cap of Object.keys(permissions) as Array<keyof typeof permissions>) {
      ret[cap] = userHasPostCap(user?.user, permissions[cap as keyof typeof permissions]) ?? false;

      // refine the capabilities if a post author is provided
      // we don't want users to edit or delete other users posts if they don't have the capability
      if (!!postAuthor?.id && user?.id) {
        if (ret[cap] === true && cap === 'edit_others_posts' && postAuthor.id !== user.id) {
          ret[cap] = false;
        }

        if (ret[cap] === true && cap === 'delete_others_posts' && postAuthor.id !== user.id) {
          ret[cap] = false;
        }

        if (ret[cap] === true && cap === 'edit_post' && postAuthor.id !== user.id) {
          ret[cap] = false;
        }
      }
    }

    return ret;
  }, [user, type]);

  return userCaps;
};
